<template>
  <div>
    <!-- 移动端显示：点击触发选择器 -->
    <div 
      v-if="isMobile"
      class="city-picker-mobile"
      @click="showPicker = true">
      <div class="city-picker-display">
        <span :class="{ 'placeholder': !value }">{{ value || placeholder }}</span>
        <i class="el-icon-arrow-down"></i>
      </div>
    </div>
    
    <!-- 桌面端显示：使用原来的 select -->
    <el-select 
      v-else
      v-model="localValue"
      :placeholder="placeholder"
      filterable
      :filter-method="handleFilterMethod"
      :popper-append-to-body="true"
      :reserve-keyword="true"
      :default-first-option="false"
      class="form-select city-select"
      @change="handleChange"
      @keydown.enter.native="handleEnter"
      @visible-change="handleVisibleChange">
      <el-option
        v-for="city in filteredCities"
        :key="city"
        :label="city"
        :value="city">
      </el-option>
    </el-select>
    
    <!-- 移动端选择器弹窗 - 类似 iOS UIPickerView -->
    <transition name="picker-fade">
      <div 
        v-if="isMobile && showPicker"
        class="picker-overlay"
        @click="showPicker = false">
        <div 
          class="picker-wrapper"
          @click.stop>
          <div class="picker-header">
            <span class="picker-cancel" @click="handleCancel">取消</span>
            <span class="picker-title">{{ title }}</span>
            <span class="picker-confirm" @click="handleConfirm">确定</span>
          </div>
          <div class="picker-body">
            <!-- 省份选择列 -->
            <div class="picker-column-wrapper">
              <div 
                class="picker-column"
                ref="provinceColumn"
                @scroll="handleProvinceScroll">
                <div 
                  v-for="(province, index) in provinces" 
                  :key="province.name"
                  :class="['picker-item', { 'selected': index === selectedProvinceIndex }]"
                  :data-index="index">
                  {{ province.name }}
                </div>
              </div>
            </div>
            <!-- 城市选择列 -->
            <div class="picker-column-wrapper">
              <div 
                class="picker-column"
                ref="cityColumn"
                @scroll="handleCityScroll">
                <div 
                  v-for="(city, index) in currentCities" 
                  :key="city"
                  :class="['picker-item', { 'selected': index === selectedCityIndex }]"
                  :data-index="index">
                  {{ city }}
                </div>
              </div>
            </div>
            <!-- 选中指示线 -->
            <div class="picker-mask">
              <div class="picker-mask-top"></div>
              <div class="picker-mask-bottom"></div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { provinces, findProvinceAndCity, getAllCities } from '../utils/provinces'
import { pinyin } from '../utils/pinyin'

export default {
  name: 'CityPicker',
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '请选择城市'
    },
    title: {
      type: String,
      default: '选择城市'
    },
    filterMethod: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      provinces: provinces,
      allCities: getAllCities(),
      localValue: this.value,
      showPicker: false,
      selectedProvinceIndex: 0,
      selectedCityIndex: 0,
      keyword: '',
      isMobile: false,
      isScrolling: {
        province: false,
        city: false
      }
    }
  },
  computed: {
    currentCities() {
      return this.provinces[this.selectedProvinceIndex]?.cities || []
    },
    filteredCities() {
      if (!this.keyword) {
        return this.allCities
      }
      return this.filterCities(this.keyword)
    }
  },
  watch: {
    value(newVal) {
      this.localValue = newVal
      if (newVal) {
        this.initSelection(newVal)
      }
    },
    localValue(newVal) {
      this.$emit('input', newVal)
    },
    showPicker(newVal) {
      if (newVal) {
        // 打开时初始化选中项
        this.initSelection(this.value || this.localValue)
        this.$nextTick(() => {
          this.scrollToSelected()
        })
      }
    },
    selectedProvinceIndex() {
      // 省份改变时，重置城市选择为第一个
      this.selectedCityIndex = 0
      this.$nextTick(() => {
        this.scrollToIndex('city', 0)
      })
    }
  },
  mounted() {
    this.detectMobile()
    window.addEventListener('resize', this.detectMobile)
    
    // 初始化选中项
    if (this.value) {
      this.initSelection(this.value)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectMobile)
  },
  methods: {
    detectMobile() {
      this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768
    },
    initSelection(cityName) {
      if (!cityName) {
        this.selectedProvinceIndex = 0
        this.selectedCityIndex = 0
        return
      }
      
      const result = findProvinceAndCity(cityName)
      if (result) {
        this.selectedProvinceIndex = result.provinceIndex
        this.selectedCityIndex = result.cityIndex
      } else {
        this.selectedProvinceIndex = 0
        this.selectedCityIndex = 0
      }
    },
    handleFilterMethod(keyword) {
      this.keyword = keyword
      if (this.filterMethod) {
        this.filterMethod(keyword)
      }
    },
    filterCities(keyword) {
      if (!keyword) {
        return this.allCities
      }
      
      const lowerKeyword = keyword.toLowerCase()
      return this.allCities.filter(city => {
        if (city.includes(keyword)) {
          return true
        }
        
        const cityPinyin = pinyin.getFullSpell(city).toLowerCase()
        if (cityPinyin && cityPinyin.includes(lowerKeyword)) {
          return true
        }
        
        const initials = pinyin.getInitials(city).toLowerCase()
        if (initials && initials.includes(lowerKeyword)) {
          return true
        }
        
        return false
      })
    },
    handleChange(value) {
      this.$emit('change', value)
    },
    handleEnter(event) {
      const select = this.$el.querySelector('.el-select')
      if (!select) return
      
      const input = select.querySelector('.el-input__inner')
      const inputValue = input ? input.value : ''
      
      if (!inputValue) return
      
      const filteredCities = this.filteredCities
      if (filteredCities && filteredCities.length > 0) {
        let selectedCity = filteredCities.find(city => city === inputValue)
        if (!selectedCity) {
          selectedCity = filteredCities[0]
        }
        this.localValue = selectedCity
        this.$emit('change', selectedCity)
      }
    },
    handleVisibleChange(visible) {
      // 桌面端下拉框打开/关闭处理
    },
    handleProvinceScroll() {
      if (this.isScrolling.province) return
      
      this.isScrolling.province = true
      clearTimeout(this.provinceScrollTimer)
      this.provinceScrollTimer = setTimeout(() => {
        this.updateProvinceSelection()
        this.isScrolling.province = false
      }, 100)
    },
    handleCityScroll() {
      if (this.isScrolling.city) return
      
      this.isScrolling.city = true
      clearTimeout(this.cityScrollTimer)
      this.cityScrollTimer = setTimeout(() => {
        this.updateCitySelection()
        this.isScrolling.city = false
      }, 100)
    },
    updateProvinceSelection() {
      const column = this.$refs.provinceColumn
      if (!column) return
      
      const scrollTop = column.scrollTop
      const itemHeight = 44
      const index = Math.round(scrollTop / itemHeight)
      
      if (index >= 0 && index < this.provinces.length) {
        this.selectedProvinceIndex = index
      }
    },
    updateCitySelection() {
      const column = this.$refs.cityColumn
      if (!column) return
      
      const scrollTop = column.scrollTop
      const itemHeight = 44
      const index = Math.round(scrollTop / itemHeight)
      
      if (index >= 0 && index < this.currentCities.length) {
        this.selectedCityIndex = index
      }
    },
    scrollToIndex(type, index) {
      this.$nextTick(() => {
        const column = type === 'province' ? this.$refs.provinceColumn : this.$refs.cityColumn
        if (!column) return
        
        const itemHeight = 44
        const scrollTop = index * itemHeight
        
        column.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      })
    },
    scrollToSelected() {
      this.scrollToIndex('province', this.selectedProvinceIndex)
      this.$nextTick(() => {
        this.scrollToIndex('city', this.selectedCityIndex)
      })
    },
    handleConfirm() {
      const selectedCity = this.currentCities[this.selectedCityIndex]
      if (selectedCity) {
        this.localValue = selectedCity
        this.$emit('change', selectedCity)
      }
      this.showPicker = false
    },
    handleCancel() {
      // 恢复之前的选中项
      this.initSelection(this.value || this.localValue)
      this.showPicker = false
    }
  }
}
</script>

<style scoped>
/* 桌面端样式 - 确保与 QuoteForm 一致 */
.city-picker-mobile {
  width: 100%;
}

/* 确保桌面端 el-select 样式正确 */
.form-select.city-select {
  width: 100%;
}

.form-select.city-select >>> .el-input__inner {
  height: 48px;
  line-height: 48px;
  border: none;
  border-radius: 0;
  font-size: 14px;
  color: #606266;
  padding: 0 15px;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

.form-select.city-select >>> .el-input__inner:hover {
  background-color: #f5f7fa;
}

.form-select.city-select >>> .el-input__inner:focus {
  background-color: #fff;
  box-shadow: inset 0 0 0 2px #75c8fc;
}

.form-select.city-select >>> .el-input.is-focus .el-input__inner {
  border: none;
}

.form-select.city-select >>> .el-input__inner::placeholder {
  color: #c0c4cc;
}

.city-picker-display {
  height: 48px;
  line-height: 48px;
  padding: 0 15px;
  border: none;
  border-radius: 0;
  font-size: 14px;
  color: #606266;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;
}

.city-picker-display:hover {
  background-color: #f5f7fa;
}

.city-picker-display .placeholder {
  color: #c0c4cc;
}

.city-picker-display i {
  color: #c0c4cc;
  font-size: 14px;
}

/* 选择器遮罩层 */
.picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: flex-end;
}

/* 选择器容器 */
.picker-wrapper {
  width: 100%;
  background: #fff;
  border-radius: 20px 20px 0 0;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* 头部 */
.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #e4e7ed;
  background: #fff;
}

.picker-cancel,
.picker-confirm {
  font-size: 16px;
  color: #409eff;
  cursor: pointer;
  user-select: none;
  padding: 5px 10px;
}

.picker-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

/* 选择器主体 */
.picker-body {
  position: relative;
  height: 300px;
  overflow: hidden;
  display: flex;
  background: #fff;
}

/* 列容器 */
.picker-column-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.picker-column {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 130px 0; /* 上下留白，让选中项居中 */
}

.picker-item {
  height: 44px;
  line-height: 44px;
  font-size: 16px;
  color: #606266;
  text-align: center;
  user-select: none;
  transition: color 0.2s;
}

.picker-item.selected {
  color: #409eff;
  font-weight: 500;
}

/* 选中指示线（类似 iOS） */
.picker-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.picker-mask-top,
.picker-mask-bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 130px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 100%);
  pointer-events: none;
}

.picker-mask-top {
  top: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 100%);
}

.picker-mask-bottom {
  bottom: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 100%);
}

/* 选中区域指示线 */
.picker-mask::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 44px;
  transform: translateY(-50%);
  border-top: 1px solid #e4e7ed;
  border-bottom: 1px solid #e4e7ed;
  pointer-events: none;
}

/* 过渡动画 */
.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.3s;
}

.picker-fade-enter,
.picker-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .city-picker-display {
    height: 44px;
    line-height: 44px;
    font-size: 13px;
    padding: 0 12px;
  }
  
  .picker-body {
    height: 250px;
  }
  
  .picker-column {
    padding: 103px 0;
  }
  
  .picker-mask-top,
  .picker-mask-bottom {
    height: 103px;
  }
}
</style>
